import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { AuthProvider } from './auth/AuthProvider';
import { ToastProvider } from './components/ui/Toast';
import { ApiError } from './api/client';
import { loadConfig } from './config';
import './styles.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // Retrying a 401/403/404 only delays the error the user needs to see.
      retry: (failureCount, error) =>
        !(error instanceof ApiError && error.status < 500) && failureCount < 2,
    },
  },
});

const root = createRoot(document.getElementById('root')!);

// Configuration must resolve before anything can call the API or Cognito, so
// the app boots behind it rather than threading "not ready yet" through the UI.
loadConfig()
  .then(() => {
    root.render(
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthProvider>
              <ToastProvider>
                <App />
              </ToastProvider>
            </AuthProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </StrictMode>,
    );
  })
  .catch((err: unknown) => {
    root.render(
      <div className="centered">
        <div className="card">
          <h2>Configuration error</h2>
          <p className="field-error">{err instanceof Error ? err.message : String(err)}</p>
        </div>
      </div>,
    );
  });
